import React, { useState } from 'react';
import { Image, X } from 'lucide-react';

interface ImageSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  images?: Array<{id: string; url: string; name: string}>;
  className?: string;
}

export default function ImageSelector({ 
  isOpen, 
  onClose, 
  onSelect,
  images = [],
  className = ''
}: ImageSelectorProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Mock images if none provided
  const mockImages = [
    {
      id: '1',
      url: 'https://images.unsplash.com/photo-1677442135968-6d89485dd1d1',
      name: 'AI Concept'
    },
    {
      id: '2',
      url: 'https://images.unsplash.com/photo-1591453089816-0fbb971b454c',
      name: 'Training Workshop'
    },
    {
      id: '3',
      url: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
      name: 'Project Management'
    }
  ];

  const displayImages = images.length > 0 ? images : mockImages;

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 ${className}`}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Select Image</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {displayImages.map((image) => (
              <button
                key={image.id}
                onClick={() => setSelectedImage(image.url)}
                className={`group relative aspect-square rounded-lg overflow-hidden border ${
                  selectedImage === image.url
                    ? 'border-[#F98B3D] ring-2 ring-[#F98B3D]'
                    : 'border-gray-200 hover:border-[#F98B3D]'
                } focus:outline-none focus:ring-2 focus:ring-[#F98B3D]`}
              >
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity" />
              </button>
            ))}
          </div>

          {displayImages.length === 0 && (
            <div className="text-center py-12">
              <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No images available</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedImage) {
                onSelect(selectedImage);
                onClose();
              }
            }}
            disabled={!selectedImage}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              !selectedImage
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#F98B3D] text-white hover:bg-[#e07a2c]'
            }`}
          >
            Select Image
          </button>
        </div>
      </div>
    </div>
  );
}